import { z } from 'zod'

const nullableTrimmedString = z.string().trim().min(1).max(256).nullable()

export const AttributionSchema = z.object({
  landingPath: z.string().trim().min(1).max(256),
  referrerDomain: nullableTrimmedString,
  utmSource: nullableTrimmedString,
  utmMedium: nullableTrimmedString,
  utmCampaign: nullableTrimmedString,
  utmContent: nullableTrimmedString,
  utmTerm: nullableTrimmedString,
  approvedAdId: nullableTrimmedString,
  referralCampaignId: nullableTrimmedString,
})

export const WaitlistSubscribeRequestSchema = z.object({
  email: z.string().trim().email().max(320),
  launchEmailConsent: z.literal(true),
  marketingConsent: z.boolean().default(false),
  attribution: AttributionSchema,
  idempotencyKey: z.string().trim().min(1).max(160),
})

export const WaitlistSubscribeResponseSchema = z.object({
  status: z.literal('verification_required'),
  verificationEmailQueued: z.boolean(),
})

export const CounselApprovedConfigurationSchema = z.object({
  version: z.string().trim().min(1).max(80),
  rulesUrl: z.string().url(),
  rulesHash: z.string().trim().min(8).max(160),
  sponsorName: z.string().trim().min(1).max(160),
  controllerName: z.string().trim().min(1).max(160),
  prizeArvCents: z.number().int().min(0),
  noPurchaseMethod: z.string().trim().min(1).max(160),
  retentionPolicyVersion: z.string().trim().min(1).max(80),
  counselApprovalPointer: z.string().trim().min(1).max(160),
  allowedCountries: z.array(z.string().regex(/^[A-Z]{2}$/)).min(1),
  localRequirementsPointer: z.string().trim().min(1).max(160),
  idempotencyKey: z.string().trim().min(1).max(160),
})

export const EligibilityCheckRequestSchema = z.object({
  subscriberId: z.string().uuid(),
  countryCode: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/),
  ageAttested: z.boolean(),
  rulesVersion: z.string().trim().min(1),
  rulesHash: z.string().trim().min(1),
})

export const EligibilityResultSchema = z.discriminatedUnion('eligible', [
  z.object({ eligible: z.literal(true), configVersion: z.string(), rulesHash: z.string() }),
  z.object({ eligible: z.literal(false), reason: z.enum(['CONFIG_NOT_OPEN', 'COUNTRY_NOT_ALLOWED', 'AGE_ATTESTATION_REQUIRED', 'RULES_MISMATCH']) }),
])

export const GiveawayEligibilityRouteRequestSchema = z.object({
  countryCode: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/),
  ageAttested: z.boolean(),
  rulesVersion: z.string().trim().min(1),
  rulesHash: z.string().trim().min(1),
  // Cloudflare Turnstile response token. Optional: absent means the client could
  // not solve a challenge (no-JS, blocked script), which the API grades as
  // `missing` and throttles rather than blocks.
  turnstileToken: z.string().trim().min(1).max(2048).optional(),
})

export const GiveawayEntryRouteRequestSchema = GiveawayEligibilityRouteRequestSchema.extend({
  giveawayConsent: z.boolean(),
  marketingConsent: z.boolean().default(false),
  idempotencyKey: z.string().trim().min(1).max(160),
})

export const GiveawayEntryResponseSchema = z.object({
  entered: z.literal(true),
  eligible: z.literal(true),
  baseEntryCreated: z.boolean().optional(),
}).strict()

export const GiveawayEntryErrorCodeSchema = z.enum([
  'COUNTRY_NOT_ALLOWED',
  'AGE_ATTESTATION_REQUIRED',
  'RULES_MISMATCH',
  'CONFIG_NOT_OPEN',
  'GIVEAWAY_CONSENT_REQUIRED',
  'GIVEAWAY_NOT_ALLOWED',
])

export const ReferralVisitRouteRequestSchema = z.object({
  referralCode: z.string().trim().regex(/^[A-Za-z0-9_-]{32,128}$/),
  visitorAttributionId: z.string().trim().min(1).max(160),
  idempotencyKey: z.string().trim().min(1).max(160),
})

export type CounselApprovedConfiguration = z.infer<typeof CounselApprovedConfigurationSchema>
export type EligibilityCheckRequest = z.infer<typeof EligibilityCheckRequestSchema>
export type EligibilityResult = z.infer<typeof EligibilityResultSchema>
export type GiveawayEligibilityRouteRequest = z.infer<typeof GiveawayEligibilityRouteRequestSchema>
export type GiveawayEntryResponse = z.infer<typeof GiveawayEntryResponseSchema>
export type GiveawayEntryErrorCode = z.infer<typeof GiveawayEntryErrorCodeSchema>
export type GiveawayEntryRouteRequest = z.infer<typeof GiveawayEntryRouteRequestSchema>
export type ReferralVisitRouteRequest = z.infer<typeof ReferralVisitRouteRequestSchema>

export type Attribution = z.infer<typeof AttributionSchema>
export type WaitlistSubscribeRequest = z.infer<typeof WaitlistSubscribeRequestSchema>
export type WaitlistSubscribeResponse = z.infer<typeof WaitlistSubscribeResponseSchema>
